import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import {
    ShieldCheck,
    CreditCard,
    AlertCircle,
    CheckCircle2,
    X
} from 'lucide-react';
import lmsApi from '../../services/api';
import { API_BASE_URL, DIGIO_ENVIRONMENT } from '@/config';
import { isWithinTimeWindow } from '../../utils/timeUtils';
import { toast } from 'sonner';

const GatewaySimulation = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const loanId = searchParams.get('loan_id');
    const action = searchParams.get('action');
    const amount = searchParams.get('amount');
    const gateway = searchParams.get('gateway') || 'sim';

    const isDigioWindowOpen = isWithinTimeWindow(6, 0, 20, 0); // Requirement 4: 6 AM - 8 PM
    // On UAT (sandbox) bypass the time-window so testers can verify the full flow at any hour.
    // WARNING: This bypass must NOT be present in production.
    const isUatBypass = DIGIO_ENVIRONMENT === 'sandbox';
    const canPay = isDigioWindowOpen || isUatBypass;

    const handleResult = async (success: boolean) => {
        if (!success) {
            toast.error('Payment Failed at Gateway');
            window.close();
            return;
        }

        setLoading(true);
        try {
            await lmsApi.post(`/sim/action/`, {
                loan_id: loanId,
                action,
                amount: parseFloat(amount || '0')
            });
            toast.success('Payment Successful!');
            // In a real scenario, this would redirect back to the app with a token
            // Since we're in a new window, we'll just close it after a brief delay
            setTimeout(() => {
                window.close();
            }, 1000);
        } catch (err: any) {
            const backendMsg = err?.response?.data?.error || err?.response?.data?.detail || err?.message;
            toast.error(backendMsg ? `Failed to finalize transaction: ${backendMsg}` : 'Failed to finalize transaction');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <Card className="max-w-md w-full shadow-2xl border-none">
                <CardHeader className="bg-slate-900 text-white rounded-t-xl text-center pb-8">
                    <div className="flex justify-center mb-4">
                        <div className="bg-primary/20 p-3 rounded-full">
                            <ShieldCheck className="h-8 w-8 text-primary" />
                        </div>
                    </div>
                    <CardTitle className="text-xl font-bold">Secure Payment Gateway</CardTitle>
                    <CardDescription className="text-slate-400">
                        {gateway === 'paytm' ? 'Paytm Checkout (Simulation)' : 'Simulation Mode'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="p-8 space-y-6">
                    <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 space-y-4">
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-slate-500 font-medium">Merchant</span>
                            <span className="text-slate-900 font-bold uppercase tracking-tight">Intellend Capital</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-slate-500 font-medium">Gateway</span>
                            <Badge variant="secondary" className="uppercase font-bold">{gateway}</Badge>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-slate-500 font-medium">Order Reference</span>
                            <span className="text-slate-900 font-mono text-xs">{loanId}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-slate-500 font-medium">Purpose</span>
                            <Badge variant="outline" className="bg-white">{action}</Badge>
                        </div>
                        <div className="h-px bg-slate-200" />
                        <div className="flex justify-between items-center">
                            <span className="text-lg font-bold text-slate-900">Total Amount</span>
                            <span className="text-2xl font-black text-primary">₹{Number(amount).toLocaleString()}</span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {!isDigioWindowOpen && (
                            <Alert className={isUatBypass ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-50 border-slate-200 text-slate-700"}>
                                <AlertCircle className={`h-4 w-4 ${isUatBypass ? 'text-amber-500' : 'text-slate-400'}`} />
                                <AlertTitle className="text-xs font-bold">OUTSIDE OPERATING HOURS</AlertTitle>
                                <AlertDescription className="text-[10px]">
                                    Outstanding payments can be made from 6:00 AM to 8:00 PM IST. Past this window we will automatically adjust your next installment.
                                    {isUatBypass && (
                                        <span className="block mt-1 font-semibold">
                                            ⚠️ UAT bypass active — buttons are enabled for testing only. This will be blocked in production.
                                        </span>
                                    )}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                            <CreditCard className="h-3 w-3" /> Select Payment Outcome
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Button
                                onClick={() => handleResult(true)}
                                disabled={loading || !canPay}
                                className="h-20 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg flex flex-col gap-1 border-b-4 border-emerald-700 active:border-b-0 active:translate-y-1 transition-all"
                            >
                                <CheckCircle2 className="h-6 w-6" />
                                PASS
                            </Button>
                            <Button
                                onClick={() => handleResult(false)}
                                disabled={loading || !canPay}
                                className="h-20 bg-rose-500 hover:bg-rose-600 text-white font-bold text-lg flex flex-col gap-1 border-b-4 border-rose-700 active:border-b-0 active:translate-y-1 transition-all"
                            >
                                <AlertCircle className="h-6 w-6" />
                                FAIL
                            </Button>
                        </div>
                    </div>
                </CardContent>
                <CardFooter className="bg-slate-50 rounded-b-xl border-t p-4 justify-center">
                    <p className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                        <Lock className="h-3 w-3" /> 256-bit SSL Encrypted Transaction
                    </p>
                </CardFooter>
            </Card>
        </div>
    );
};

// Mock Lock icon if not imported
const Lock = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
);

export default GatewaySimulation;
